import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export interface ConnectionRequest {
  id: string;
  requester_id: string;
  recipient_id: string;
  requester_role: string;
  recipient_role: string;
  status: string;
  message: string | null;
  scheduled_at: string | null;
  zoom_link: string | null;
  created_at: string;
  admin_approved_at: string | null;
  admin_rejection_reason_public: string | null;
  requester: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    role: string;
  };
  recipient: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    role: string;
  };
  // Keep backward compatibility
  entrepreneur_id?: string;
  mentor_id?: string;
}

export function useConnections() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["connections", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Use connections table
      const { data, error } = await supabase
        .from("connections")
        .select(`
          id,
          requester_id,
          recipient_id,
          status,
          message,
          scheduled_at,
          zoom_link,
          created_at,
          admin_approved_at,
          admin_rejection_reason_public,
          connection_type,
          requester:profiles!connections_requester_id_fkey(id, full_name, avatar_url, role),
          recipient:profiles!connections_recipient_id_fkey(id, full_name, avatar_url, role)
        `)
        .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      // Map data with roles derived from profiles
      return (data || []).map(c => ({
        ...c,
        requester_role: (c.requester as any)?.role || "unknown",
        recipient_role: (c.recipient as any)?.role || "unknown",
        requester: c.requester || { id: c.requester_id, full_name: "Unknown", avatar_url: null, role: "unknown" },
        recipient: c.recipient || { id: c.recipient_id, full_name: "Unknown", avatar_url: null, role: "unknown" },
      })) as ConnectionRequest[];
    },
    enabled: !!user?.id,
  });
}

export function useCreateConnectionRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ recipientId, message }: { recipientId: string; message?: string }) => {
      const { data, error } = await supabase.rpc("create_connection_request", {
        p_recipient_id: recipientId,
        p_message: message || null,
      });

      if (error) throw error;

      // The RPC catches its own exceptions and returns
      // { success: false, error } with HTTP 200, so `error` is null even when
      // the request failed. Nothing used to check this, so failures — duplicate
      // request, self-connection, not authenticated — all reported success.
      const result = data as { success?: boolean; error?: string } | null;
      if (!result?.success) {
        throw new Error(result?.error || "Failed to send connection request");
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connections"] });
      // Don't show toast here - let the caller handle context-aware messaging
    },
    onError: (error) => {
      toast.error("Failed to send request: " + error.message);
    },
  });
}

export function useRespondToConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      requestId, 
      accept, 
      connection 
    }: { 
      requestId: string; 
      accept: boolean; 
      connection?: ConnectionRequest;
    }) => {
      const updates: Record<string, unknown> = {
        status: accept ? "accepted" : "declined",
        updated_at: new Date().toISOString(),
      };

      if (accept) {
        updates.accepted_at = new Date().toISOString();
        
        if (connection) {
          // Create Zoom meeting for accepted connections
          const requesterName = connection.requester?.full_name || "Participant";
          const recipientName = connection.recipient?.full_name || "Participant";
          
          const zoomResponse = await supabase.functions.invoke("create-zoom-meeting", {
            body: {
              topic: `Connection Call: ${requesterName} & ${recipientName}`,
              duration: 60,
            },
          });

          if (zoomResponse.data?.join_url) {
            updates.zoom_link = zoomResponse.data.join_url;
          }
        }
      }

      // Use connections table
      const { error } = await supabase
        .from("connections")
        .update(updates)
        .eq("id", requestId);

      if (error) throw error;
    },
    onSuccess: (_, { accept }) => {
      queryClient.invalidateQueries({ queryKey: ["connections"] });
      toast.success(accept ? "Connection accepted!" : "Connection declined.");
    },
    onError: (error) => {
      toast.error("Failed to respond: " + error.message);
    },
  });
}

export function useAdminPendingConnections() {
  return useQuery({
    queryKey: ["admin_pending_connections"],
    queryFn: async () => {
      // Fetch pending mentorship connections that need admin approval
      const { data, error } = await supabase
        .from("connections")
        .select(`
          id,
          requester_id,
          recipient_id,
          status,
          message,
          created_at,
          scheduled_at,
          zoom_link,
          admin_approved_at,
          admin_rejection_reason_public,
          connection_type,
          requester:profiles!connections_requester_id_fkey(id, full_name, avatar_url, role, bio),
          recipient:profiles!connections_recipient_id_fkey(id, full_name, avatar_url, role, bio)
        `)
        .eq("status", "pending")
        .eq("connection_type", "mentorship")
        .order("created_at", { ascending: true });

      if (error) throw error;
      
      // Map data with roles derived from profiles
      return (data || []).map(c => ({
        ...c,
        requester_role: (c.requester as any)?.role || "unknown",
        recipient_role: (c.recipient as any)?.role || "unknown",
        requester: c.requester || { id: c.requester_id, full_name: "Unknown", avatar_url: null, role: "unknown" },
        recipient: c.recipient || { id: c.recipient_id, full_name: "Unknown", avatar_url: null, role: "unknown" },
      })) as (ConnectionRequest & { requester: ConnectionRequest["requester"] & { bio?: string } })[];
    },
  });
}

export function useAdminProcessConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      requestId, 
      action, 
      rejectionReason 
    }: { 
      requestId: string; 
      action: "approve" | "reject"; 
      rejectionReason?: string;
    }) => {
      const { error } = await supabase.rpc("admin_process_connection_request", {
        p_request_id: requestId,
        p_action: action,
        p_rejection_reason: rejectionReason || null,
      });

      if (error) throw error;
    },
    onSuccess: (_, { action }) => {
      queryClient.invalidateQueries({ queryKey: ["admin_pending_connections"] });
      queryClient.invalidateQueries({ queryKey: ["connections"] });
      toast.success(action === "approve" ? "Connection approved!" : "Connection rejected.");
    },
    onError: (error) => {
      toast.error("Failed to process: " + error.message);
    },
  });
}

export function useExistingConnection(otherUserId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["existing_connection", user?.id, otherUserId],
    queryFn: async () => {
      if (!user?.id || !otherUserId) return null;

      // Check connections table only
      const { data, error } = await supabase
        .from("connections")
        .select("id, status")
        .or(`and(requester_id.eq.${user.id},recipient_id.eq.${otherUserId}),and(requester_id.eq.${otherUserId},recipient_id.eq.${user.id})`)
        .in("status", ["pending", "accepted"])
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id && !!otherUserId,
  });
}
